/* draw-animation/index.js — the editor: wires the page to the model.
 *
 *   doc.js       the animation as data (frames, background, history)
 *   rows.js      how a layer's pixels are kept
 *   surface.js   the canvases, onion ghosts, frame rendering
 *   tools.js     brush · eraser · shapes · bucket · picker
 *   select.js    lift / move / scale / rotate / paste
 *   timeline.js  the strip of frames
 *   export.js    GIF · video · PNG archive · sprite sheet
 *
 * The visible canvas is a pure function of the state: every change calls
 * invalidate(), and one requestAnimationFrame paints paper → background →
 * onion ghosts → frame → the stroke in progress. Nothing else draws on it —
 * which is also what keeps the ink under the pen DURING a stroke (the previous
 * version painted only on release).
 */

import {
    createDoc, current, commit, resetHistory, undo, redo, canUndo, canRedo, go, addFrame, removeFrame,
    moveFrame, reverseFrames, setHold, setRows, rowsOf, resize, ticks, durationMs, tickMs,
    usedBytes, toState, fromState, formatOf, FORMATS, DEFAULT_FORMAT, MAX_FRAMES, MAX_HOLD,
} from './doc.js';
import { pack, unpack } from './rows.js';
import { createSurface, refitRows } from './surface.js';
import { createTools, rgbToHex } from './tools.js';
import { createSelect } from './select.js';
import { createTimeline } from './timeline.js';
import { runExport, videoSupport } from './export.js';
import { buildDemo } from './demo.js';
import { initFunnel, emit } from '../toy-fx/telemetry.js';

const SLUG = 'draw-animation';
const I18N = (typeof window !== 'undefined' && window.DrawAnimationI18n) || {};
const $ = (id) => document.getElementById(id);
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const WIDE = 980;               /* container width from which settings are a column */
const PEN_QUIET_MS = 20000;     /* after a pen stroke, a lone finger is a palm */

const app = {
    doc: null,
    surf: null,
    tool: 'brush',
    target: 'frame',            /* which layer the tools draw on: 'frame' | 'bg' */
    color: '#111827',
    recent: [],
    size: 10,
    opacity: 1,
    smooth: 0.35,
    shape: 'line',
    shapeFill: false,
    selMode: 'rect',
    fillGap: true,
    onion: { back: 1, fwd: 0, alpha: 0.35 },
    view: { s: 1, fit: 1, zoom: 1, panX: 0, panY: 0, tx: 0, ty: 0, cw: 0, ch: 0 },
    stroke: null,               /* {mode, alpha} while a stroke is in progress */
    float: null,                /* the selected piece being moved */
    clip: null,                 /* what «copy» took */
    playing: false,
    playFrame: -1,
    busy: false,
    maxFrames: MAX_FRAMES,
    t: (k) => I18N[k] || k,
};

let els = {};
let viewCtx = null;
let overlayCtx = null;
let tools = null;
let select = null;
let timeline = null;
/* Panels are opened and placed by the shared primitive (js/anchored-popover.js);
 * the editor only ever needs to shut them. */
const pops = {
    close(panel) {
        try { if (panel && window.__anchoredPopover) window.__anchoredPopover.close(panel); } catch (_) {}
    },
    closeAll() {
        document.querySelectorAll('#da .da-pop').forEach((p) => pops.close(p));
    },
};
let drafts = null;
let raf = 0;
let gesture = null;             /* the pointer that is drawing right now */
let hover = null;               /* last mouse/pen position over the stage, sheet px */
let prevTool = 'brush';
let spaceDown = false;
/* When a pen last touched the sheet. «Never» must be far in the past: with 0
 * here every finger was taken for a palm during the page's first 20 seconds. */
let penAt = -Infinity;
let videoKind;                  /* undefined until asked; then 'mp4' | 'webm' | null */
let lastFile = null;            /* the last exported file, for «share» */
let abortExport = null;
let tipped = {};
const touches = new Map();
let pinch = null;

function ae(f, extra) { emit(SLUG, f, extra); }

/* a number the way the page's language writes it: «0,1 с», not «0.1 с» */
function num(v, digits) {
    try {
        return v.toLocaleString(window.__LOCALE || undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
    } catch (_) {
        return v.toFixed(digits);
    }
}
function stage(name) { try { if (window.__stage) window.__stage(name); } catch (_) {} }
function toast(msg, type) { try { if (window.__toast) window.__toast(msg, type || 'info'); } catch (_) {} }

/* ── state the modules reach for ──────────────────────────────────────── */

app.layer = () => app.surf[app.target];
app.layerRows = () => rowsOf(app.doc, app.target);
app.gesturing = () => !!gesture || !!pinch;

app.invalidate = () => {
    if (!raf) raf = requestAnimationFrame(render);
};

/** A layer canvas changed in rows [y0, y1): store it and record the edit. */
app.commitLayer = (y0, y1) => {
    let rows;
    try {
        rows = app.surf.take(app.target, y0, y1);
    } catch (err) {
        /* The browser refused to read the canvas back (Safari out of canvas
         * memory answers with InvalidStateError). The step cannot be recorded —
         * so it must not stay on screen either: what is shown is what is saved. */
        ae('commit_fail', { err: String((err && err.name) || err).slice(0, 40) });
        app.surf.live[app.target] = null;
        app.surf.show(app.target, app.layerRows());
        toast(app.t('mem_full'), 'warning');
        app.invalidate();
        return;
    }
    if (setRows(app.doc, app.target, rows)) {
        app.surf.live[app.target] = rowsOf(app.doc, app.target);
        edited();
    } else {
        app.invalidate();
    }
};

app.tip = (key) => {
    /* a hint is said once per page load — repeated, it is nagging */
    if (tipped[key]) return;
    tipped[key] = true;
    toast(app.t(key), 'info');
};

app.floatChanged = () => {
    els.selbar.hidden = !app.float;
    els.selHint.hidden = !!app.float;
    app.invalidate();
};

app.goTo = (i) => {
    if (i < 0 || i >= app.doc.frames.length) return;
    settle();
    if (go(app.doc, i)) showFrame();
};

app.addFrame = (copy) => {
    settle();
    if (app.doc.frames.length >= MAX_FRAMES) {
        app.tip('max_frames');
        return;
    }
    if (usedBytes(app.doc) > memoryBudget()) {
        toast(app.t('mem_full'), 'warning');
        return;
    }
    if (addFrame(app.doc, { copy })) {
        ae(copy ? 'frame_dup' : 'frame_add', { n: app.doc.frames.length });
        app.surf.show('frame', current(app.doc).rows);
        edited();
        timeline.reveal();
    }
};

app.moveFrameTo = (from, to) => {
    settle();
    if (moveFrame(app.doc, from, to)) {
        ae('frame_reorder', { from, to });
        app.surf.show('frame', current(app.doc).rows);
        edited();
        timeline.reveal();
    }
};

app.setColor = (hex, opts = {}) => {
    if (!/^#[0-9a-f]{6}$/i.test(hex)) return;
    app.color = hex.toLowerCase();
    els.root.style.setProperty('--da-ink', app.color);
    if (els.colorIn.value !== app.color) els.colorIn.value = app.color;
    document.querySelectorAll('[data-da-color]').forEach((b) => b.classList.toggle('on', b.dataset.daColor === app.color));
    if (opts.remember !== false) rememberColor(app.color);
    els.recent.querySelectorAll('.da-sw').forEach((b) => b.classList.toggle('on', b.dataset.c === app.color));
};

app.pickerDone = () => setTool(prevTool === 'picker' ? 'brush' : prevTool);

/* ── view: fit, zoom, pan ─────────────────────────────────────────────── */

function measureAvail() {
    /* How much of the screen is ours: the viewport minus the site's fixed bars
     * (promo strip, mobile header). The CSS has an estimate; this is the fact. */
    let top = 0;
    document.querySelectorAll('.pro-banner, header.header').forEach((el) => {
        const cs = getComputedStyle(el);
        if (cs.position !== 'fixed' && cs.position !== 'sticky') return;
        const r = el.getBoundingClientRect();
        if (r.height > 0 && r.top <= 4) top = Math.max(top, r.bottom);
    });
    els.root.style.setProperty('--da-avail', Math.max(320, window.innerHeight - top - 12) + 'px');
}

function fitView() {
    const v = app.view;
    const cw = els.stage.clientWidth;
    const ch = els.stage.clientHeight;
    if (!cw || !ch) return;
    const pad = cw > 520 ? 10 : 0;
    v.cw = cw;
    v.ch = ch;
    v.fit = Math.min((cw - pad * 2) / app.doc.w, (ch - pad * 2) / app.doc.h);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (els.overlay.width !== Math.round(cw * dpr) || els.overlay.height !== Math.round(ch * dpr)) {
        els.overlay.width = Math.round(cw * dpr);
        els.overlay.height = Math.round(ch * dpr);
    }
    v.dpr = dpr;
    applyView();
}

function applyView() {
    const v = app.view;
    v.s = v.fit * v.zoom;
    const w = app.doc.w * v.s;
    const h = app.doc.h * v.s;
    /* the sheet may leave the stage only as far as still leaves it reachable */
    const mx = Math.max(0, (w - v.cw) / 2 + 24);
    const my = Math.max(0, (h - v.ch) / 2 + 24);
    v.panX = w > v.cw ? clamp(v.panX, -mx, mx) : 0;
    v.panY = h > v.ch ? clamp(v.panY, -my, my) : 0;
    v.tx = (v.cw - w) / 2 + v.panX;
    v.ty = (v.ch - h) / 2 + v.panY;
    els.canvas.style.width = w + 'px';
    els.canvas.style.height = h + 'px';
    els.canvas.style.transform = 'translate(' + v.tx + 'px,' + v.ty + 'px)';
    const pct = app.t('zoom_pct').replace(':n', Math.round(v.zoom * 100));
    els.zoomFit.textContent = pct;
    els.zoomReset.textContent = pct;
    /* on a narrow console the only zoom control is the way back from a pinch */
    els.zoomChip.hidden = v.zoom <= 1.01;
    app.invalidate();
}

function zoomAt(factor, cx, cy) {
    const v = app.view;
    const before = v.zoom;
    const next = clamp(before * factor, 1, 12);
    if (next === before) return;
    /* keep the sheet point under the cursor where it is */
    const px = (cx - v.tx) / v.s;
    const py = (cy - v.ty) / v.s;
    v.zoom = next;
    const s = v.fit * next;
    v.panX = cx - px * s - (v.cw - app.doc.w * s) / 2;
    v.panY = cy - py * s - (v.ch - app.doc.h * s) / 2;
    applyView();
}

function stagePoint(e) {
    const r = els.stage.getBoundingClientRect();
    return { x: e.clientX - r.left - els.stage.clientLeft, y: e.clientY - r.top - els.stage.clientTop };
}

function docPoint(e) {
    const p = stagePoint(e);
    const v = app.view;
    return {
        x: (p.x - v.tx) / v.s,
        y: (p.y - v.ty) / v.s,
        p: e.pointerType === 'pen' && e.pressure > 0 ? e.pressure : 1,
    };
}

const toScreen = (d) => ({ x: d.x * app.view.s + app.view.tx, y: d.y * app.view.s + app.view.ty });

/* ── painting ─────────────────────────────────────────────────────────── */

function layerPass(c, name) {
    const S = app.surf;
    const L = S[name];
    const mine = app.target === name;
    const st = mine ? app.stroke : null;
    if (!S.live[name] && !st && !(mine && app.float)) return;     /* empty layer, nothing in flight */
    if (st && st.mode === 'erase') {
        /* preview of the eraser: the layer with the stroke cut out of it */
        const m = S.mix.ctx;
        m.setTransform(1, 0, 0, 1, 0, 0);
        m.globalAlpha = 1;
        m.globalCompositeOperation = 'copy';
        m.drawImage(L.canvas, 0, 0);
        m.globalCompositeOperation = 'destination-out';
        m.drawImage(S.stroke.canvas, 0, 0);
        m.globalCompositeOperation = 'source-over';
        c.drawImage(S.mix.canvas, 0, 0);
    } else {
        c.drawImage(L.canvas, 0, 0);
        if (st) {
            c.globalAlpha = st.alpha;
            c.drawImage(S.stroke.canvas, 0, 0);
            c.globalAlpha = 1;
        }
    }
    if (mine && app.float) select.paintFloat(c, app.float);
}

function render() {
    raf = 0;
    const d = app.doc;
    const S = app.surf;
    const c = viewCtx;
    if (app.playing) {
        S.renderFrame(c, d, app.playFrame, d.w, d.h, { liveIndex: d.index });
    } else {
        c.setTransform(1, 0, 0, 1, 0, 0);
        c.globalAlpha = 1;
        c.globalCompositeOperation = 'source-over';
        c.fillStyle = d.paper;
        c.fillRect(0, 0, d.w, d.h);
        layerPass(c, 'bg');
        /* ghosts are a guide for the FRAME — while the background is being
         * drawn they would only be in the way */
        if (app.target === 'frame' && (app.onion.back || app.onion.fwd)) {
            S.buildOnion(d.frames, d.index, app.onion);
            if (!S.onionEmpty) c.drawImage(S.onion.canvas, 0, 0);
        }
        layerPass(c, 'frame');
    }
    paintOverlay();
}

function paintOverlay() {
    const o = overlayCtx;
    const v = app.view;
    o.setTransform(v.dpr || 1, 0, 0, v.dpr || 1, 0, 0);
    o.clearRect(0, 0, v.cw, v.ch);
    if (app.playing) return;
    select.drawOverlay(o, toScreen);
    /* the brush as a ring of its real size — mouse and pen only, a finger covers it */
    if (hover && !app.float && (app.tool === 'brush' || app.tool === 'eraser')) {
        const r = Math.max(1.5, (app.size * v.s) / 2);
        const p = toScreen(hover);
        o.beginPath();
        o.arc(p.x, p.y, r, 0, Math.PI * 2);
        o.lineWidth = 2;
        o.strokeStyle = 'rgba(255,255,255,.9)';
        o.stroke();
        o.lineWidth = 1;
        o.strokeStyle = 'rgba(15,23,42,.9)';
        o.stroke();
    }
}

function setCursor() {
    let c = 'cross';
    if (spaceDown || (gesture && gesture.pan)) c = 'grab';
    else if (app.tool === 'select' && hover) c = select.cursorAt(hover);
    else if ((app.tool === 'brush' || app.tool === 'eraser') && hover && app.size * app.view.s >= 8) c = 'none';
    if (els.stage.dataset.cursor !== c) els.stage.dataset.cursor = c;
}

/* ── editing flow ─────────────────────────────────────────────────────── */

/** settle — finish whatever is half-done before the document is read or changes. */
function settle() {
    stopPlayback();
    if (gesture && gesture.tool) {
        gesture.tool.cancel();
        gesture = null;
        delete els.stage.dataset.drawing;
    }
    select.drop();
}

/** edited — the document changed: one undo step, and everything that shows it. */
function edited() {
    commit(app.doc);
    refresh();
    if (drafts) drafts.touch(true);
}

function refresh() {
    const d = app.doc;
    timeline.sync();
    els.undo.disabled = !canUndo(d);
    els.redo.disabled = !canRedo(d);
    const i = d.index + 1;
    const secs = durationMs(d) / 1000;
    els.count.innerHTML = '';
    const b = document.createElement('span');
    b.textContent = app.t('frame_of').replace(':i', i).replace(':n', d.frames.length);
    const dur = document.createElement('i');
    dur.className = 'da-dur';
    dur.textContent = ' · ' + app.t('duration_s').replace(':s', num(secs, secs < 10 ? 1 : 0));
    els.count.append(b, dur);

    const f = current(d);
    els.holdOut.textContent = app.t('hold_badge').replace(':n', f.hold);
    els.holdLess.disabled = f.hold <= 1;
    els.holdMore.disabled = f.hold >= MAX_HOLD;
    els.moveLeft.disabled = d.index === 0;
    els.moveRight.disabled = d.index === d.frames.length - 1;
    els.add.disabled = d.frames.length >= MAX_FRAMES;
    els.dup.disabled = d.frames.length >= MAX_FRAMES;
    els.del.disabled = d.frames.length === 1 && !f.rows && f.hold === 1;
    els.paste.disabled = !app.clip;
    els.reverse.disabled = d.frames.length < 2;
    els.playBtn.disabled = d.frames.length < 2;

    const empty = d.frames.length === 1 && !f.rows && !d.bg && !app.float;
    els.empty.hidden = !empty;
    app.invalidate();
}

function showFrame() {
    app.surf.show('frame', current(app.doc).rows);
    commit(app.doc);            /* no edit — only remembers the frame in hand */
    refresh();
    timeline.reveal();
}

/** After undo/redo or a loaded work: make every canvas and control match the doc. */
function adopt() {
    const d = app.doc;
    const S = app.surf;
    if (S.w !== d.w || S.h !== d.h) {
        S.resize(d.w, d.h);
        els.canvas.width = d.w;
        els.canvas.height = d.h;
        app.view.zoom = 1;
        app.view.panX = 0;
        app.view.panY = 0;
    }
    els.root.style.setProperty('--da-w', String(d.w));
    els.root.style.setProperty('--da-h', String(d.h));
    S.show('bg', d.bg);
    S.show('frame', current(d).rows);
    syncDocControls();
    /* the stage's box follows the aspect ratio — fit after layout has caught up */
    requestAnimationFrame(fitView);
    refresh();
    timeline.reveal();
}

function syncDocControls() {
    const d = app.doc;
    const fmt = formatOf(d);
    document.querySelectorAll('[data-da-format]').forEach((b) => press(b, b.dataset.daFormat === fmt));
    document.querySelectorAll('[data-da-paper]').forEach((b) => b.classList.toggle('on', b.dataset.daPaper === d.paper));
    if (/^#[0-9a-f]{6}$/i.test(d.paper)) els.paperIn.value = d.paper;
    document.querySelectorAll('[data-da-loop]').forEach((b) => press(b, b.dataset.daLoop === d.loop));
    if (String(els.fps.value) !== String(d.fps)) {
        els.fps.value = String(d.fps);
        /* a speed above this plan's ceiling: let the gate move it down and say so */
        if (window.__qualityTier && window.__qualityTier.gate) {
            try { window.__qualityTier.gate(els.fps, 'fps'); } catch (_) {}
        }
        const v = parseInt(els.fps.value, 10);
        if (v && v !== d.fps) d.fps = v;
    }
}

function press(btn, on) {
    btn.classList.toggle('on', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
}

function doUndo(back) {
    stopPlayback();
    /* with a piece in the air, «undo» means «put it back» */
    if (app.float || select.busy()) {
        select.tool.cancel();
        select.cancel();
        return;
    }
    if (back ? undo(app.doc) : redo(app.doc)) {
        ae(back ? 'undo' : 'redo', {});
        adopt();
        if (drafts) drafts.touch(true);
    }
}

function memoryBudget() {
    const gb = (window.__memBudget && window.__memBudget.gb()) || 4;
    return clamp(gb * 96e6, 192e6, 768e6);
}

/* ── tools ────────────────────────────────────────────────────────────── */

function setTool(name) {
    if (!tools[name] && name !== 'select') return;
    if (name === app.tool) return;
    if (app.tool === 'select') {
        select.tool.cancel();
        select.drop();
    }
    if (app.tool !== 'picker') prevTool = app.tool;
    app.tool = name;
    els.root.dataset.tool = name;
    document.querySelectorAll('[data-da-tool]').forEach((b) => press(b, b.dataset.daTool === name));
    document.querySelectorAll('[data-da-for]').forEach((el) => {
        el.hidden = !el.dataset.daFor.split(' ').includes(name);
    });
    ae('tool_pick', { tool: name });
    setCursor();
    app.invalidate();
}

function setTarget(name) {
    if (name === app.target) return;
    settle();
    app.target = name;
    document.querySelectorAll('[data-da-layer]').forEach((b) => press(b, b.dataset.daLayer === name));
    els.stage.dataset.layer = name;
    els.layerBadge.hidden = name !== 'bg';
    ae('layer_pick', { layer: name });
    app.invalidate();
}

function rememberColor(hex) {
    if (document.querySelector('[data-da-color="' + hex + '"]')) return;    /* already a swatch */
    app.recent = [hex].concat(app.recent.filter((c) => c !== hex)).slice(0, 6);
    paintRecent();
    try { window.__toolSettings && window.__toolSettings.save(SLUG, 'recent', app.recent); } catch (_) {}
}

function paintRecent() {
    els.recent.textContent = '';
    app.recent.forEach((hex) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'da-sw';
        b.style.setProperty('--c', hex);
        b.dataset.c = hex;
        b.classList.toggle('on', hex === app.color);
        b.setAttribute('aria-label', hex);
        b.addEventListener('click', () => {
            app.setColor(hex, { remember: false });
            if (app.tool === 'eraser' || app.tool === 'picker') setTool('brush');
            pops.close($('da-pop-color'));
        });
        els.recent.appendChild(b);
    });
}

/* ── pointer input ────────────────────────────────────────────────────── */

function toolFor(e) {
    if (e.pointerType === 'pen' && (e.buttons & 32)) return tools.eraser;      /* the pen's eraser end */
    if (e.altKey && (app.tool === 'brush' || app.tool === 'fill' || app.tool === 'shape')) {
        prevTool = app.tool;
        return tools.picker;
    }
    return app.tool === 'select' ? select.tool : tools[app.tool];
}

function onDown(e) {
    if (app.busy) return;
    if (e.target.closest && e.target.closest('.da-float, .da-empty .da-b, .da-busy')) return;

    if (e.pointerType === 'touch') {
        touches.set(e.pointerId, { x: e.clientX, y: e.clientY, x0: e.clientX, y0: e.clientY });
        if (touches.size === 2) {
            /* a second finger: whatever the first one started is not a stroke */
            if (gesture) {
                if (gesture.tool) gesture.tool.cancel();
                gesture = null;
                delete els.stage.dataset.drawing;
            }
            startPinch();
            e.preventDefault();
            return;
        }
        if (touches.size > 2) return;
        if (performance.now() - penAt < PEN_QUIET_MS) return;       /* palm */
    }
    if (gesture) return;
    if (e.pointerType === 'mouse' && e.button !== 0 && e.button !== 1) return;
    if (e.pointerType === 'pen') penAt = performance.now();

    try { els.stage.setPointerCapture(e.pointerId); } catch (_) {}
    e.preventDefault();
    stopPlayback();
    pops.closeAll();
    els.done.hidden = true;

    if (e.button === 1 || spaceDown) {
        gesture = { id: e.pointerId, pan: true, x: e.clientX, y: e.clientY, px: app.view.panX, py: app.view.panY };
        setCursor();
        return;
    }
    const tool = toolFor(e);
    if (!tool) return;
    gesture = { id: e.pointerId, tool };
    els.stage.dataset.drawing = '';
    hover = e.pointerType === 'touch' ? null : docPoint(e);
    tool.down(docPoint(e), e);
}

function onMove(e) {
    if (e.pointerType === 'touch' && touches.has(e.pointerId)) {
        const t = touches.get(e.pointerId);
        t.x = e.clientX;
        t.y = e.clientY;
        if (pinch) {
            movePinch();
            e.preventDefault();
            return;
        }
    }
    if (gesture && e.pointerId === gesture.id) {
        if (gesture.pan) {
            app.view.panX = gesture.px + (e.clientX - gesture.x);
            app.view.panY = gesture.py + (e.clientY - gesture.y);
            panned = true;
            applyView();
            return;
        }
        /* Coalesced events are the samples the browser merged into this one —
         * without them a fast stroke on a 60 Hz screen comes out angular. */
        const list = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : null;
        const evs = list && list.length ? list : [e];
        for (const ev of evs) gesture.tool.move(docPoint(ev), e);
        if (e.pointerType !== 'touch') hover = docPoint(e);
        e.preventDefault();
        return;
    }
    if (!gesture && e.pointerType !== 'touch') {
        hover = docPoint(e);
        setCursor();
        app.invalidate();
    }
}

function onUp(e) {
    const wasTouch = touches.has(e.pointerId);
    if (wasTouch) {
        const t = touches.get(e.pointerId);
        if (pinch) pinch.travel = Math.max(pinch.travel, Math.hypot(t.x - t.x0, t.y - t.y0));
        touches.delete(e.pointerId);
        if (pinch && touches.size < 2) endPinch();
    }
    if (!gesture || e.pointerId !== gesture.id) return;
    const g = gesture;
    gesture = null;
    delete els.stage.dataset.drawing;
    if (g.pan) {
        setCursor();
        return;
    }
    if (e.type === 'pointerup') g.tool.up(docPoint(e), e);
    else g.tool.cancel();
    if (e.pointerType === 'pen') penAt = performance.now();
    setCursor();
}

function startPinch() {
    const [a, b] = Array.from(touches.values());
    const r = els.stage.getBoundingClientRect();
    pinch = {
        d0: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        zoom0: app.view.zoom,
        mx: (a.x + b.x) / 2 - r.left,
        my: (a.y + b.y) / 2 - r.top,
        t0: performance.now(),
        travel: 0,
        moved: false,
    };
}

function movePinch() {
    if (touches.size < 2) return;
    const [a, b] = Array.from(touches.values());
    const r = els.stage.getBoundingClientRect();
    const d = Math.hypot(a.x - b.x, a.y - b.y) || 1;
    const mx = (a.x + b.x) / 2 - r.left;
    const my = (a.y + b.y) / 2 - r.top;
    const ratio = d / pinch.d0;
    if (!pinch.moved && Math.abs(ratio - 1) < 0.04 && Math.hypot(mx - pinch.mx, my - pinch.my) < 8) return;
    pinch.moved = true;
    const v = app.view;
    /* zoom about the midpoint of the fingers, and let the midpoint drag the sheet */
    zoomAt((pinch.zoom0 * ratio) / v.zoom, mx, my);
    v.panX += mx - pinch.mx;
    v.panY += my - pinch.my;
    pinch.mx = mx;
    pinch.my = my;
    applyView();
}

function endPinch() {
    const p = pinch;
    pinch = null;
    /* two fingers down and up, without travelling: «undo», as in every drawing app */
    if (!p.moved && performance.now() - p.t0 < 320 && p.travel < 14) doUndo(true);
}

/* ── playback ─────────────────────────────────────────────────────────── */

let playRaf = 0;

function startPlayback() {
    if (app.playing || app.doc.frames.length < 2 || app.busy) return;
    settle();
    const line = ticks(app.doc);
    const step = tickMs(app.doc);
    const once = app.doc.loop === 'once';
    const t0 = performance.now();
    app.playing = true;
    app.playFrame = line[0];
    ae('preview_play', { frames: app.doc.frames.length, fps: app.doc.fps, loop: app.doc.loop });
    press(els.playBtn, true);
    els.playBtn.querySelector('.da-ico-play').hidden = true;
    els.playBtn.querySelector('.da-ico-stop').hidden = false;
    els.playBtn.setAttribute('aria-label', app.t('stop'));

    /* Wall-clock driven: a frame that took long to decode is followed by the
     * frame that is DUE, not by the next one — speed never drifts. */
    const tick = () => {
        if (!app.playing) return;
        const n = Math.floor((performance.now() - t0) / step);
        if (once && n >= line.length) {
            stopPlayback();
            return;
        }
        const i = line[n % line.length];
        if (i !== app.playFrame) {
            app.playFrame = i;
            app.invalidate();
        }
        timeline.playing(i);
        playRaf = requestAnimationFrame(tick);
    };
    app.invalidate();
    playRaf = requestAnimationFrame(tick);
}

function stopPlayback() {
    if (!app.playing) return;
    app.playing = false;
    if (playRaf) cancelAnimationFrame(playRaf);
    playRaf = 0;
    timeline.playing(-1);
    press(els.playBtn, false);
    els.playBtn.querySelector('.da-ico-play').hidden = false;
    els.playBtn.querySelector('.da-ico-stop').hidden = true;
    els.playBtn.setAttribute('aria-label', app.t('play'));
    app.invalidate();
}

/* ── sheet ────────────────────────────────────────────────────────────── */

function setFormat(key) {
    const f = FORMATS[key];
    const d = app.doc;
    if (!f || (f.w === d.w && f.h === d.h)) return;
    settle();
    const hadInk = !!d.bg || d.frames.some((x) => x.rows);
    const from = { w: d.w, h: d.h };
    resize(d, f.w, f.h, (rows) => refitRows(rows, from, f));
    ae('format_set', { format: key });
    try { window.__toolSettings && window.__toolSettings.save(SLUG, 'format', key); } catch (_) {}
    app.surf.resize(d.w, d.h);
    els.canvas.width = d.w;
    els.canvas.height = d.h;
    app.view.zoom = 1;
    app.view.panX = 0;
    app.view.panY = 0;
    app.clip = null;
    commit(d);
    adopt();
    if (drafts) drafts.touch(true);
    if (hadInk) app.tip('format_confirm');
}

function setPaper(hex) {
    if (!/^#[0-9a-f]{6}$/i.test(hex) || hex === app.doc.paper) return;
    app.doc.paper = hex.toLowerCase();
    syncDocControls();
    edited();
}

/* ── images in ────────────────────────────────────────────────────────── */

function insertImage(file) {
    if (!file) return;
    settle();
    const done = (src, w, h) => {
        if (!(w > 0 && h > 0)) {
            toast(app.t('image_fail'), 'error');
            return;
        }
        setTool('select');
        select.fromImage(src, w, h);
        pops.closeAll();
        ae('image_insert', { w, h });
        if (src.close) { try { src.close(); } catch (_) {} }
    };
    if (window.createImageBitmap) {
        /* imageOrientation: phone photos carry their rotation in EXIF */
        createImageBitmap(file, { imageOrientation: 'from-image' })
            .then((bm) => done(bm, bm.width, bm.height))
            .catch(() => toast(app.t('image_fail'), 'error'));
        return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { done(img, img.naturalWidth, img.naturalHeight); URL.revokeObjectURL(url); };
    img.onerror = () => { URL.revokeObjectURL(url); toast(app.t('image_fail'), 'error'); };
    img.src = url;
}

/* ── export ───────────────────────────────────────────────────────────── */

function setBusy(on, label) {
    app.busy = on;
    els.busy.hidden = !on;
    if (label) els.busyT.textContent = label;
    els.fill.style.width = '0%';
    els.progress.setAttribute('aria-valuenow', '0');
    els.exportBtn.disabled = on;
}

async function doExport(format) {
    if (app.busy) return;
    settle();
    pops.closeAll();
    const d = app.doc;
    const ctl = new AbortController();
    abortExport = ctl;
    lastFile = null;
    els.done.hidden = true;
    setBusy(true, app.t('exporting'));
    stage('export_start');
    const started = performance.now();
    const opts = {
        gifSize: parseInt(els.gifSize.value, 10) || 640,
        colors: parseInt(els.colors.value, 10) || 128,
        videoP: parseInt(els.vres.value, 10) || 720,
        transparent: !!els.transparent.checked,
        basename: 'animation',
        signal: ctl.signal,
        onProgress: (v) => {
            const pct = Math.round(v * 100);
            els.fill.style.width = pct + '%';
            els.progress.setAttribute('aria-valuenow', String(pct));
        },
    };
    try {
        const res = await runExport(app, format, opts);
        let ok = !!res.delivered;
        if (res.blob) {
            /* the verdict of the save, not the fact that we asked for one */
            ok = (await window.__downloadFile(res.blob, res.filename)) !== false;
        }
        stage('export_done');
        const bytes = res.blob ? res.blob.size : 0;
        ae('export_ok', { fmt: res.filename.split('.').pop(), kind: format, frames: res.frames, bytes, fps: d.fps });
        try {
            window.__aeToolComplete && window.__aeToolComplete({
                format: res.filename.split('.').pop(),
                frames: res.frames,
                bytes,
                fps: d.fps,
                ms: Math.round(performance.now() - started),
            });
        } catch (_) {}
        if (ok) {
            if (drafts) drafts.exported();
            showDone(res);
        }
    } catch (err) {
        const cancelled = window.__isUserCancel ? window.__isUserCancel(err, ctl.signal) : (err && err.name === 'AbortError');
        if (!cancelled) {
            ae('export_fail', { kind: format, err: String((err && (err.code || err.message)) || err).slice(0, 80) });
            if (err && err.code === 'gif_ram_budget') {
                /* say which lever helps, with the number that would fit */
                toast(app.t('gif_too_big').replace(':n', err.maxFrames), 'warning');
            } else if (err && err.code === 'video_unsupported') {
                toast(app.t('video_unsupported'), 'warning');
            } else if (window.__handleError) {
                window.__handleError(err, 'draw-animation:export', { say: app.t('export_fail') });
            } else {
                toast(app.t('export_fail'), 'error');
            }
        }
    } finally {
        abortExport = null;
        setBusy(false);
        app.invalidate();
    }
}

function showDone(res) {
    /* what was made and how heavy it is — the name is in the downloads list anyway */
    const kb = res.blob ? Math.max(1, Math.round(res.blob.size / 1024)) : 0;
    const kind = res.filename.split('.').pop().toUpperCase();
    els.doneT.textContent = app.t('export_done') + ' · ' + kind
        + (kb ? ' · ' + (kb >= 1024 ? num(kb / 1024, 1) + ' MB' : kb + ' KB') : '');
    lastFile = null;
    els.share.hidden = true;
    if (res.blob && navigator.canShare) {
        try {
            const f = new File([res.blob], res.filename, { type: res.blob.type || 'application/octet-stream' });
            if (navigator.canShare({ files: [f] })) {
                lastFile = f;
                els.share.hidden = false;
            }
        } catch (_) {}
    }
    /* The site already says «file saved». This note earns its place only where
     * it adds a verb: sending the file straight to another app. */
    els.done.hidden = !lastFile;
}

/* ── «my work» ────────────────────────────────────────────────────────── */

const blobOf = new WeakMap();       /* rows → the bytes they are stored as */
let preview = { at: null, url: '' };

function draftPayload() {
    const d = app.doc;
    const files = [];
    const at = new Map();
    const fileIndex = (rows) => {
        if (!at.has(rows)) {
            let b = blobOf.get(rows);
            if (!b) {
                b = new Blob([pack(rows, d.w, d.h)], { type: 'application/octet-stream' });
                blobOf.set(rows, b);
            }
            at.set(rows, files.length);
            files.push(b);
        }
        return at.get(rows);
    };
    const state = toState(d, fileIndex);
    state.color = app.color;
    const snap = d.history[d.cursor];
    if (preview.at !== snap) preview = { at: snap, url: timeline.previewUrl() };
    return {
        state,
        files,
        label: app.t('export_frames').replace(':n', d.frames.length),
        preview: preview.url,
        meta: { frames: d.frames.length, w: d.w, h: d.h },
    };
}

function draftApply(saved) {
    const files = saved.files || [];
    return Promise.all(files.map((f) => {
        if (!f) return null;
        const read = f.arrayBuffer ? f.arrayBuffer() : new Response(f).arrayBuffer();
        return read.then((buf) => ({ layer: unpack(buf), file: f })).catch(() => null);
    })).then((layers) => {
        const missing = [];
        const next = fromState(saved.state, (i) => {
            const L = layers[i];
            if (!L) {
                missing.push('layer-' + i);
                return null;
            }
            /* the restored rows ARE these bytes — an untouched layer is not rewritten */
            blobOf.set(L.layer.rows, L.file);
            return L.layer.rows;
        });
        load(next);
        if (saved.state && saved.state.color) app.setColor(saved.state.color, { remember: false });
        return missing;
    });
}

function draftFresh() {
    load(createDoc({ format: savedFormat(), fps: 12 }));
}

function draftStarted() {
    const d = app.doc;
    return d.cursor > 0 || d.frames.length > 1 || !!d.bg || !!d.frames[0].rows || !!app.float || !!gesture;
}

function savedFormat() {
    let f = DEFAULT_FORMAT;
    try { f = (window.__toolSettings && window.__toolSettings.load(SLUG, 'format', DEFAULT_FORMAT)) || DEFAULT_FORMAT; } catch (_) {}
    return FORMATS[f] ? f : DEFAULT_FORMAT;
}

/** load — replace the whole document (a saved work, a new one, the example). */
function load(next) {
    stopPlayback();
    if (gesture && gesture.tool) gesture.tool.cancel();
    gesture = null;
    delete els.stage.dataset.drawing;
    app.float = null;
    els.selbar.hidden = true;
    els.selHint.hidden = false;
    app.stroke = null;
    app.doc = next;
    preview = { at: null, url: '' };
    adopt();
}

function loadDemo() {
    const d = createDoc({ format: formatOf(app.doc) || DEFAULT_FORMAT });
    const demo = buildDemo(d.w, d.h);
    d.fps = demo.fps;
    d.loop = demo.loop;
    d.bg = demo.bg;
    demo.frames.forEach((f, i) => {
        if (i) addFrame(d);
        setRows(d, 'frame', f.rows);
        setHold(d, f.hold);
    });
    d.index = 0;
    resetHistory(d);
    load(d);
    ae('demo_load', {});
    if (drafts) drafts.touch(true);
    startPlayback();
}

function bootDrafts() {
    const bar = $('daDrafts');
    if (!bar) return;
    if (!window.__toolDraft || !window.__toolDraft.editor) {
        /* no store on this page — no bar that does nothing */
        const wrap = bar.closest('.da-drafts');
        if (wrap) wrap.hidden = true;
        return;
    }
    drafts = window.__toolDraft.editor({
        slug: SLUG,
        root: 'daDrafts',
        icon: '🎞️',
        payload: draftPayload,
        apply: draftApply,
        fresh: draftFresh,
        started: draftStarted,
    });
    /* «New», «My work», a row of the list: put the floating piece down first,
     * so what is saved is what is on screen. Capture phase — before the bar acts. */
    document.addEventListener('click', (e) => {
        const t = e.target;
        if (t && t.closest && t.closest('#daDrafts, #daDraftsNote')) settle();
    }, true);
    drafts.boot();
}

/* ── keyboard ─────────────────────────────────────────────────────────── */

const TOOL_KEYS = { b: 'brush', e: 'eraser', g: 'fill', u: 'shape', s: 'select', i: 'picker' };

/* Keys are ours only while the editor is what the person is looking at — the
 * page below it has an FAQ to read and to select text in. */
function editorInView() {
    const r = els.root.getBoundingClientRect();
    return r.bottom > 120 && r.top < window.innerHeight - 120;
}

function typingIn(el) {
    const tag = ((el && el.tagName) || '').toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'select' || !!(el && el.isContentEditable);
}

function onControl(el) {
    const tag = ((el && el.tagName) || '').toLowerCase();
    return tag === 'button' || tag === 'a' || tag === 'summary';
}

function onKey(e) {
    if (typingIn(e.target) || app.busy || !editorInView()) return;
    const key = e.key;
    const k = key.length === 1 ? key.toLowerCase() : key;
    const meta = e.ctrlKey || e.metaKey;

    if (meta) {
        /* text selected on the page: Ctrl+C / X / A belong to it */
        const picked = window.getSelection ? String(window.getSelection()) : '';
        if (k === 'z') { e.preventDefault(); doUndo(!e.shiftKey); }
        else if (k === 'y') { e.preventDefault(); doUndo(false); }
        else if (picked) return;
        else if (k === 'c') { if (select.copy()) { e.preventDefault(); refresh(); } }
        else if (k === 'x') { if (app.float && select.copy()) { e.preventDefault(); select.remove(); refresh(); } }
        else if (k === 'a') { e.preventDefault(); setTool('select'); select.selectAll(); }
        /* Ctrl+V is handled by the `paste` event: it also carries images */
        return;
    }
    if (e.altKey) return;

    if (key === ' ') {
        /* a button focused from the keyboard keeps its own Space */
        if (onControl(e.target)) return;
        e.preventDefault();
        if (e.repeat) return;
        /* Space is «play» on a tap and «hold to pan» when held with a drag */
        spaceDown = true;
        setCursor();
        return;
    }
    if (app.float) {
        const step = e.shiftKey ? 10 : 1;
        if (key === 'ArrowLeft') { e.preventDefault(); select.nudge(-step, 0); return; }
        if (key === 'ArrowRight') { e.preventDefault(); select.nudge(step, 0); return; }
        if (key === 'ArrowUp') { e.preventDefault(); select.nudge(0, -step); return; }
        if (key === 'ArrowDown') { e.preventDefault(); select.nudge(0, step); return; }
        if (key === 'Enter') { e.preventDefault(); select.drop(); return; }
        if (key === 'Escape') { e.preventDefault(); select.cancel(); return; }
        if (key === 'Delete' || key === 'Backspace') { e.preventDefault(); select.remove(); return; }
    }
    if (key === 'ArrowLeft' || key === ',') { e.preventDefault(); app.goTo(app.doc.index - 1); }
    else if (key === 'ArrowRight' || key === '.') { e.preventDefault(); app.goTo(app.doc.index + 1); }
    else if (key === 'Home') { e.preventDefault(); app.goTo(0); }
    else if (key === 'End') { e.preventDefault(); app.goTo(app.doc.frames.length - 1); }
    else if (k === 'n') app.addFrame(false);
    else if (k === 'd') app.addFrame(true);
    else if (key === '[') setSize(app.size - (app.size > 20 ? 4 : 1));
    else if (key === ']') setSize(app.size + (app.size >= 20 ? 4 : 1));
    else if (key === '+' || key === '=') changeHold(1);
    else if (key === '-' || key === '_') changeHold(-1);
    else if (key === 'Escape' && gesture && gesture.tool) { gesture.tool.cancel(); gesture = null; delete els.stage.dataset.drawing; }
    else if (TOOL_KEYS[k]) setTool(TOOL_KEYS[k]);
}

function onKeyUp(e) {
    if (e.key !== ' ' || !spaceDown) return;
    spaceDown = false;
    setCursor();
    /* released without having dragged the sheet → it was «play / stop» */
    if (!gesture) {
        if (typingIn(e.target) || onControl(e.target)) return;
        if (panned) {
            panned = false;
            return;
        }
        app.playing ? stopPlayback() : startPlayback();
    }
}
let panned = false;

function setSize(v) {
    app.size = clamp(Math.round(v), 1, 120);
    els.size.value = String(app.size);
    els.sizeOut.textContent = String(app.size);
    try { window.__toolSettings && window.__toolSettings.save(SLUG, 'size', app.size); } catch (_) {}
    app.invalidate();
}

function changeHold(delta) {
    settle();
    if (setHold(app.doc, current(app.doc).hold + delta)) {
        ae('hold_set', { hold: current(app.doc).hold });
        edited();
    }
}

/* ── wiring ───────────────────────────────────────────────────────────── */

function on(el, type, fn, opts) {
    if (el) el.addEventListener(type, fn, opts);
}

function bind() {
    const TS = window.__toolSettings;

    /* tools */
    document.querySelectorAll('[data-da-tool]').forEach((b) => on(b, 'click', () => setTool(b.dataset.daTool)));
    document.querySelectorAll('[data-da-layer]').forEach((b) => on(b, 'click', () => setTarget(b.dataset.daLayer)));
    document.querySelectorAll('[data-da-shape]').forEach((b) => on(b, 'click', () => {
        app.shape = b.dataset.daShape;
        document.querySelectorAll('[data-da-shape]').forEach((x) => press(x, x === b));
    }));
    on(els.shapeFill, 'click', () => {
        app.shapeFill = !app.shapeFill;
        press(els.shapeFill, app.shapeFill);
    });
    document.querySelectorAll('[data-da-sel]').forEach((b) => on(b, 'click', () => {
        app.selMode = b.dataset.daSel;
        document.querySelectorAll('[data-da-sel]').forEach((x) => press(x, x === b));
    }));
    on(els.fillGap, 'click', () => {
        app.fillGap = !app.fillGap;
        press(els.fillGap, app.fillGap);
    });

    /* brush options */
    if (TS) TS.bind(SLUG, els.size, 'size', 10);
    on(els.size, 'input', () => {
        app.size = clamp(parseInt(els.size.value, 10) || 10, 1, 120);
        els.sizeOut.textContent = String(app.size);
        app.invalidate();
    });
    app.size = clamp(parseInt(els.size.value, 10) || 10, 1, 120);
    els.sizeOut.textContent = String(app.size);
    if (TS) TS.bind(SLUG, els.opacity, 'opacity', '1');
    on(els.opacity, 'change', () => { app.opacity = clamp(parseFloat(els.opacity.value) || 1, 0.05, 1); });
    app.opacity = clamp(parseFloat(els.opacity.value) || 1, 0.05, 1);
    if (TS) TS.bind(SLUG, els.smooth, 'smooth', '0.35');
    on(els.smooth, 'change', () => { app.smooth = clamp(parseFloat(els.smooth.value) || 0, 0, 0.9); });
    app.smooth = clamp(parseFloat(els.smooth.value) || 0, 0, 0.9);

    /* colour */
    const picked = () => {
        if (app.tool === 'eraser' || app.tool === 'picker') setTool('brush');
        pops.close($('da-pop-color'));      /* a no-op where the palette is a column */
    };
    document.querySelectorAll('[data-da-color]').forEach((b) => on(b, 'click', () => {
        app.setColor(b.dataset.daColor, { remember: false });
        picked();
    }));
    on(els.colorIn, 'input', () => app.setColor(els.colorIn.value, { remember: false }));
    on(els.colorIn, 'change', () => {
        app.setColor(els.colorIn.value);
        picked();
    });
    if (window.EyeDropper) {
        /* pick a colour from anywhere on the screen — Chromium on desktop */
        els.eyedrop.hidden = false;
        on(els.eyedrop, 'click', () => {
            pops.closeAll();
            new window.EyeDropper().open().then((r) => {
                if (r && r.sRGBHex) app.setColor(r.sRGBHex.slice(0, 7));
            }).catch(() => {});
        });
    }
    try {
        const saved = TS ? TS.load(SLUG, 'recent', []) : [];
        if (Array.isArray(saved)) app.recent = saved.filter((c) => /^#[0-9a-f]{6}$/i.test(c)).slice(0, 6);
    } catch (_) {}
    paintRecent();
    app.setColor(app.color, { remember: false });

    /* history */
    const hold = window.__holdRepeat;
    on(els.undo, 'click', () => doUndo(true));
    on(els.redo, 'click', () => doUndo(false));
    if (hold) {
        hold(els.undo, () => { els.undo.click(); return !els.undo.disabled; }, { delay: 400, period: 110 });
        hold(els.redo, () => { els.redo.click(); return !els.redo.disabled; }, { delay: 400, period: 110 });
    }

    /* frames */
    on(els.add, 'click', () => app.addFrame(false));
    on(els.dup, 'click', () => app.addFrame(true));
    on(els.del, 'click', () => {
        settle();
        if (removeFrame(app.doc)) {
            ae('frame_del', { n: app.doc.frames.length });
            app.surf.show('frame', current(app.doc).rows);
            edited();
            timeline.reveal();
        }
    });
    on(els.holdLess, 'click', () => changeHold(-1));
    on(els.holdMore, 'click', () => changeHold(1));
    on(els.moveLeft, 'click', () => app.moveFrameTo(app.doc.index, app.doc.index - 1));
    on(els.moveRight, 'click', () => app.moveFrameTo(app.doc.index, app.doc.index + 1));
    on(els.copy, 'click', () => {
        if (select.copy()) ae('copy', {});
        refresh();
        pops.closeAll();
    });
    on(els.paste, 'click', () => {
        pops.closeAll();
        setTool('select');
        if (select.paste()) ae('paste', {});
    });
    on(els.clear, 'click', () => {
        pops.closeAll();
        settle();
        if (!app.layerRows()) return;
        app.layer().ctx.clearRect(0, 0, app.doc.w, app.doc.h);
        app.commitLayer(0, app.doc.h);
    });
    on(els.reverse, 'click', () => {
        pops.closeAll();
        settle();
        if (reverseFrames(app.doc)) {
            ae('reverse', {});
            app.surf.show('frame', current(app.doc).rows);
            edited();
            timeline.reveal();
        }
    });

    /* transport */
    on(els.playBtn, 'click', () => (app.playing ? stopPlayback() : startPlayback()));
    on(els.first, 'click', () => app.goTo(0));
    on(els.prev, 'click', () => app.goTo(app.doc.index - 1));
    on(els.next, 'click', () => app.goTo(app.doc.index + 1));
    on(els.last, 'click', () => app.goTo(app.doc.frames.length - 1));
    if (hold) {
        hold(els.prev, () => { els.prev.click(); return app.doc.index > 0; }, { delay: 400, period: 90 });
        hold(els.next, () => { els.next.click(); return app.doc.index < app.doc.frames.length - 1; }, { delay: 400, period: 90 });
    }

    /* onion skin */
    const onion = () => {
        app.onion.back = clamp(parseInt(els.onionBack.value, 10) || 0, 0, 3);
        app.onion.fwd = clamp(parseInt(els.onionFwd.value, 10) || 0, 0, 3);
        app.onion.alpha = clamp((parseInt(els.onionAlpha.value, 10) || 35) / 100, 0.05, 0.8);
        app.invalidate();
    };
    if (TS) {
        TS.bind(SLUG, els.onionBack, 'onion_back', '1');
        TS.bind(SLUG, els.onionFwd, 'onion_fwd', '0');
        TS.bind(SLUG, els.onionAlpha, 'onion_alpha', '35');
    }
    on(els.onionBack, 'change', () => { onion(); ae('onion_set', { back: app.onion.back, fwd: app.onion.fwd }); });
    on(els.onionFwd, 'change', () => { onion(); ae('onion_set', { back: app.onion.back, fwd: app.onion.fwd }); });
    on(els.onionAlpha, 'input', onion);
    onion();

    /* speed */
    on(els.fps, 'change', () => {
        const v = parseInt(els.fps.value, 10);
        if (!v || v === app.doc.fps) return;
        stopPlayback();
        app.doc.fps = v;
        ae('fps_set', { fps: v });
        refresh();
        if (drafts) drafts.touch(true);
    });
    document.querySelectorAll('[data-da-loop]').forEach((b) => on(b, 'click', () => {
        if (app.doc.loop === b.dataset.daLoop) return;
        stopPlayback();
        app.doc.loop = b.dataset.daLoop;
        ae('loop_set', { loop: app.doc.loop });
        syncDocControls();
        refresh();
        if (drafts) drafts.touch(true);
    }));

    /* sheet */
    document.querySelectorAll('[data-da-format]').forEach((b) => on(b, 'click', () => setFormat(b.dataset.daFormat)));
    document.querySelectorAll('[data-da-paper]').forEach((b) => on(b, 'click', () => setPaper(b.dataset.daPaper)));
    on(els.paperIn, 'change', () => setPaper(els.paperIn.value));

    /* zoom */
    on(els.zoomIn, 'click', () => zoomAt(1.5, app.view.cw / 2, app.view.ch / 2));
    on(els.zoomOut, 'click', () => zoomAt(1 / 1.5, app.view.cw / 2, app.view.ch / 2));
    const wholeSheet = () => {
        app.view.zoom = 1;
        app.view.panX = 0;
        app.view.panY = 0;
        applyView();
    };
    on(els.zoomFit, 'click', wholeSheet);
    on(els.zoomReset, 'click', wholeSheet);
    on(els.stage, 'wheel', (e) => {
        /* only with Ctrl (and trackpad pinch, which arrives as Ctrl+wheel):
         * a plain wheel over the sheet must keep scrolling the page */
        if (!e.ctrlKey && !e.metaKey) return;
        e.preventDefault();
        const p = stagePoint(e);
        zoomAt(Math.exp(-e.deltaY * 0.0022), p.x, p.y);
    }, { passive: false });

    /* selection bar */
    on(els.selFlip, 'click', () => select.flip());
    on(els.selCopy, 'click', () => { select.copy(); refresh(); });
    on(els.selDel, 'click', () => select.remove());
    on(els.selDone, 'click', () => select.drop());

    /* pointer */
    on(els.stage, 'pointerdown', onDown);
    on(els.stage, 'pointermove', onMove);
    on(els.stage, 'pointerup', onUp);
    on(els.stage, 'pointercancel', onUp);
    on(els.stage, 'lostpointercapture', (e) => { if (gesture && e.pointerId === gesture.id) onUp(e); });
    on(els.stage, 'pointerleave', (e) => {
        if (e.pointerType === 'touch' || gesture) return;
        hover = null;
        app.invalidate();
    });
    on(els.stage, 'contextmenu', (e) => e.preventDefault());

    /* example */
    on(els.demo, 'click', loadDemo);

    /* export */
    document.querySelectorAll('[data-da-export]').forEach((b) => on(b, 'click', () => doExport(b.dataset.daExport)));
    on(els.cancel, 'click', () => { if (abortExport) abortExport.abort(); });
    if (TS) {
        TS.bind(SLUG, els.gifSize, 'gif_size', '640');
        TS.bind(SLUG, els.colors, 'colors', '128');
        TS.bind(SLUG, els.vres, 'vres', '720');
        TS.bind(SLUG, els.transparent, 'transparent', false);
    }
    on(els.share, 'click', () => {
        if (!lastFile || !navigator.share) return;
        navigator.share({ files: [lastFile] }).catch((err) => {
            if (err && err.name !== 'AbortError') toast(app.t('share_fail'), 'warning');
        });
        ae('share', { type: lastFile.type });
    });
    on(els.doneX, 'click', () => { els.done.hidden = true; });

    /* images: the shared uploader in the frame menu, a drop on the sheet, a paste */
    const up = $('daUpload');
    on(up, 'tool-uploader:file', (e) => {
        const f = e.detail && (e.detail.file || (e.detail.files && e.detail.files[0]));
        if (f) insertImage(f);
        if (up.__uploaderReset) up.__uploaderReset();
    });
    ['dragenter', 'dragover'].forEach((t) => on(els.stage, t, (e) => {
        if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes('Files')) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    }));
    on(els.stage, 'drop', (e) => {
        const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (!f) return;
        e.preventDefault();
        /* through the uploader: same size and type checks as a pick */
        if (up && up.__uploaderInjectFile) up.__uploaderInjectFile(f, 'canvas_drop');
        else insertImage(f);
    });
    on(document, 'paste', (e) => {
        if (typingIn(e.target) || app.busy || !editorInView()) return;
        const items = (e.clipboardData && e.clipboardData.items) || [];
        for (const it of items) {
            if (it.type && it.type.indexOf('image/') === 0) {
                const f = it.getAsFile();
                if (f) {
                    e.preventDefault();
                    insertImage(f);
                    return;
                }
            }
        }
        if (app.clip) {
            e.preventDefault();
            setTool('select');
            select.paste();
        }
    });

    /* A button pressed with the mouse or a finger keeps focus, and the next
     * Space would press it again instead of playing. Hand the keyboard back —
     * but only after a POINTER click (detail > 0): keyboard users keep focus. */
    on(els.root, 'click', (e) => {
        if (!e.detail) return;
        const b = e.target.closest && e.target.closest('button');
        if (b && els.root.contains(b)) b.blur();
    });

    on(document, 'keydown', onKey);
    on(document, 'keyup', onKeyUp);
    on(window, 'blur', () => { spaceDown = false; setCursor(); });
}

function watchLayout() {
    /* The console's own width decides the arrangement (a container query in the
     * CSS, same number). Colour and settings float on a narrow console and are
     * a column on a wide one — a panel left open across that line is shut, so
     * it does not stay in the top layer over the column it has just become. */
    const side = [$('da-pop-color'), $('da-pop-settings')];
    let wide = null;
    const mode = () => {
        const now = els.root.clientWidth >= WIDE;
        if (now === wide) return;
        wide = now;
        side.forEach((p) => pops.close(p));
    };
    mode();
    if (window.ResizeObserver) {
        new ResizeObserver(mode).observe(els.root);
        new ResizeObserver(() => fitView()).observe(els.stage);
    }
    on(window, 'resize', () => { measureAvail(); mode(); fitView(); });
    on(window, 'orientationchange', () => { measureAvail(); fitView(); });
}

export function mount() {
    const root = $('da');
    const canvas = $('da-canvas');
    if (!root || !canvas) return;

    els = {
        root, canvas,
        stage: $('da-stage'), overlay: $('da-overlay'), strip: $('da-strip'),
        undo: $('da-undo'), redo: $('da-redo'), exportBtn: $('da-export-btn'),
        size: $('da-size'), sizeOut: $('da-size-out'), opacity: $('da-opacity'), smooth: $('da-smooth'),
        shapeFill: $('da-shape-fill'), fillGap: $('da-fill-gap'),
        colorIn: $('da-color'), recent: $('da-recent'), eyedrop: $('da-eyedrop'),
        selbar: $('da-selbar'), selFlip: $('da-sel-flip'), selCopy: $('da-sel-copy'), selDel: $('da-sel-del'), selDone: $('da-sel-done'),
        zoomIn: $('da-zoom-in'), zoomOut: $('da-zoom-out'), zoomFit: $('da-zoom-fit'),
        zoomReset: $('da-zoom-reset'), zoomChip: $('da-zoom-chip'), layerBadge: $('da-layer-badge'), selHint: $('da-sel-hint'),
        empty: $('da-empty'), demo: $('da-demo'),
        busy: $('da-busy'), busyT: $('da-busy-t'), progress: $('da-progress'), fill: $('da-progress-fill'), cancel: $('da-cancel'),
        done: $('da-done'), doneT: $('da-done-t'), share: $('da-share'), doneX: $('da-done-x'),
        first: $('da-first'), prev: $('da-prev'), playBtn: $('da-play'), next: $('da-next'), last: $('da-last'),
        count: $('da-count'), add: $('da-add'), dup: $('da-dup'), del: $('da-del'),
        holdLess: $('da-hold-less'), holdMore: $('da-hold-more'), holdOut: $('da-hold-out'),
        moveLeft: $('da-move-left'), moveRight: $('da-move-right'),
        copy: $('da-copy'), paste: $('da-paste'), clear: $('da-clear'), reverse: $('da-reverse'),
        onionBack: $('da-onion-back'), onionFwd: $('da-onion-fwd'), onionAlpha: $('da-onion-alpha'),
        fps: $('da-fps'), paperIn: $('da-paper'),
        gifSize: $('da-gif-size'), colors: $('da-colors'), vres: $('da-vres'), transparent: $('da-transparent'),
        videoName: $('da-video-name'),
    };

    app.doc = createDoc({ format: savedFormat() });
    app.surf = createSurface(app.doc.w, app.doc.h);
    canvas.width = app.doc.w;
    canvas.height = app.doc.h;
    /* opaque (paper is always painted) and low-latency where the platform has it */
    viewCtx = canvas.getContext('2d', { alpha: false, desynchronized: true }) || canvas.getContext('2d');
    overlayCtx = els.overlay.getContext('2d');

    tools = createTools(app);
    select = createSelect(app);
    timeline = createTimeline(app, els.strip);

    bind();
    measureAvail();
    watchLayout();
    root.dataset.ready = '';
    adopt();
    fitView();

    initFunnel(SLUG);
    stage('ready');

    /* Which video container this browser can make decides the button's name.
     * Asked when the download panel is first opened: the answer costs a muxer
     * download and an encoder probe that a visitor who only draws never needs. */
    on(els.exportBtn, 'click', () => {
        videoSupport(app.doc).then((kind) => {
            videoKind = kind;
            const btn = document.querySelector('[data-da-export="video"]');
            if (els.videoName) els.videoName.textContent = app.t(kind === 'webm' ? 'video_webm' : 'video_mp4');
            if (btn) btn.disabled = !kind;
        }).catch(() => { videoKind = null; });
    }, { once: true });

    bootDrafts();

    /* a hook for the browser stands (tests/playwright) — read-only */
    window.__daTest = {
        app,
        frames: () => app.doc.frames.length,
        index: () => app.doc.index,
        used: () => usedBytes(app.doc),
        videoKind: () => videoKind,
        ink: (i) => {
            const f = app.doc.frames[i === undefined ? app.doc.index : i];
            if (!f || !f.rows) return 0;
            let n = 0;
            for (const r of f.rows) n += r.length;
            return n;
        },
        pixel: (x, y) => {
            const d = viewCtx.getImageData(x, y, 1, 1).data;
            return rgbToHex(d[0], d[1], d[2]);
        },
    };
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount, { once: true });
} else {
    mount();
}
